import { useState } from 'react';
import { uid } from '../../lib/uid';
import { ChevronDown, ChevronRight, Plus, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel,
  AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
  AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../ui/alert-dialog';
import AutocompleteInput from './AutocompleteInput';
import { formatCurrency, getHistoryItems } from '../../lib/defaultData';

export default function ScopeOfWorks({ categories, onUpdateCategories, amountEnabled = true, onToggleAmount }) {
  const [expanded, setExpanded] = useState({});
  const [focusedPriceId, setFocusedPriceId] = useState(null);
  const [focusedCommentId, setFocusedCommentId] = useState(null);
  const categoryHistory = getHistoryItems('history_categories');
  const itemHistory = getHistoryItems('history_items');

  const toggleExpand = (catId) => {
    setExpanded(prev => ({ ...prev, [catId]: prev[catId] !== false ? false : true }));
  };

  const updateCategory = (catId, field, value) => {
    onUpdateCategories(
      categories.map(c => c.id === catId ? { ...c, [field]: value } : c)
    );
  };

  const updateCategoryPrice = (catId, value) => {
    const num = parseFloat(value);
    if (!isNaN(num)) {
      updateCategory(catId, 'price', num);
    }
  };

  const addCategory = () => {
    const newCat = {
      id: uid(),
      name: '',
      price: 0,
      items: [{ id: uid(), description: '', comment: '' }],
    };
    onUpdateCategories([...categories, newCat]);
    setExpanded(prev => ({ ...prev, [newCat.id]: true }));
  };

  const deleteCategory = (catId) => {
    onUpdateCategories(categories.filter(c => c.id !== catId));
  };

  const insertCategoryAfter = (idx) => {
    const newCat = {
      id: uid(),
      name: '',
      price: 0,
      items: [{ id: uid(), description: '', comment: '' }],
    };
    const next = [...categories];
    next.splice(idx + 1, 0, newCat);
    onUpdateCategories(next);
    setExpanded(prev => ({ ...prev, [newCat.id]: true }));
  };

  const updateItem = (catId, itemId, field, value) => {
    onUpdateCategories(
      categories.map(c => {
        if (c.id !== catId) return c;
        return {
          ...c,
          items: c.items.map(item =>
            item.id === itemId ? { ...item, [field]: value } : item
          ),
        };
      })
    );
  };

  const addItem = (catId) => {
    onUpdateCategories(
      categories.map(c => {
        if (c.id !== catId) return c;
        return {
          ...c,
          items: [...c.items, { id: uid(), description: '', comment: 0 }],
        };
      })
    );
  };

  const deleteItem = (catId, itemId) => {
    onUpdateCategories(
      categories.map(c => {
        if (c.id !== catId) return c;
        return { ...c, items: c.items.filter(item => item.id !== itemId) };
      })
    );
  };

  return (
    <div className="py-8" data-testid="scope-of-works">
      <h2 className="section-heading">Scope of Works</h2>

      {/* Header row */}
      <div
        className="flex items-center px-4 py-2 text-xs font-semibold uppercase tracking-wider"
        style={{ color: '#6b7280', borderBottom: '1px solid rgba(201,168,76,0.2)' }}
      >
        <span className="flex-1">Category / Description</span>
        <span className="w-[140px] text-right pr-2 shrink-0" style={{ color: '#9ca3af' }}>
          Cost (internal)
        </span>
        <div className="flex items-center gap-2 w-[172px] justify-end shrink-0">
          <Switch
            checked={amountEnabled}
            onCheckedChange={onToggleAmount}
            data-testid="amount-enabled-toggle"
            className="scale-75 origin-right"
          />
          <span className={amountEnabled ? '' : 'opacity-40'}>Amount (AUD)</span>
        </div>
      </div>

      {categories.map((cat, catIdx) => (
        <div key={cat.id} className="animate-fade-in" data-testid={`category-${catIdx}`}>
          {/* Category Row */}
          <div className="category-row group">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <button
                onClick={() => toggleExpand(cat.id)}
                className="flex-shrink-0 text-[#1a1a2e]/50 hover:text-[#1a1a2e] transition-colors"
                data-testid={`category-toggle-${catIdx}`}
              >
                {expanded[cat.id] !== false ? (
                  <ChevronDown className="w-5 h-5" />
                ) : (
                  <ChevronRight className="w-5 h-5" />
                )}
              </button>
              <div className="flex-1 min-w-0">
                <AutocompleteInput
                  value={cat.name}
                  onChange={v => updateCategory(cat.id, 'name', v)}
                  suggestions={categoryHistory}
                  placeholder="Category Name"
                  className="w-full bg-transparent text-[#1a1a2e] font-semibold text-sm"
                  testId={`category-name-${catIdx}`}
                />
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className={`relative w-[140px] transition-opacity ${amountEnabled ? 'opacity-100' : 'opacity-40'}`}>
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#1a1a2e]/40 text-sm">$</span>
                <input
                  type="text"
                  value={focusedPriceId === cat.id ? cat.price : Number(cat.price || 0).toFixed(2)}
                  onChange={e => {
                    const raw = e.target.value.replace(/,/g, '');
                    if (raw === '' || !isNaN(raw)) {
                      updateCategory(cat.id, 'price', raw === '' ? 0 : parseFloat(raw));
                    }
                  }}
                  onFocus={() => setFocusedPriceId(cat.id)}
                  onBlur={() => { setFocusedPriceId(null); updateCategoryPrice(cat.id, cat.price); }}
                  className="editable-field amount-input pl-6 text-[#1a1a2e] bg-transparent text-sm"
                  title={amountEnabled ? undefined : 'Internal reference only — not included in Subtotal'}
                  data-testid={`category-price-${catIdx}`}
                />
              </div>
              <button
                onClick={() => insertCategoryAfter(catIdx)}
                className="text-[#1a1a2e]/20 hover:text-[#c9a84c] transition-colors flex-shrink-0 opacity-0 group-hover:opacity-100"
                title="Insert category below"
                data-testid={`category-insert-${catIdx}`}
              >
                <Plus className="w-4 h-4" />
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    className="text-[#1a1a2e]/30 hover:text-[#dc2626] transition-colors flex-shrink-0"
                    data-testid={`category-delete-${catIdx}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Category</AlertDialogTitle>
                    <AlertDialogDescription>
                      Are you sure you want to delete this category? This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => deleteCategory(cat.id)}
                      className="bg-[#dc2626] hover:bg-[#b91c1c]"
                      data-testid="delete-confirm-btn"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </div>

          {/* Items (when expanded) */}
          {expanded[cat.id] !== false && (
            <div className="animate-slide-down">
              {cat.items.map((item, itemIdx) => (
                <div key={item.id} className="item-row" data-testid={`item-${catIdx}-${itemIdx}`}>
                  <div className="flex-1 min-w-0">
                    <AutocompleteInput
                      value={item.description}
                      onChange={v => updateItem(cat.id, item.id, 'description', v)}
                      suggestions={itemHistory}
                      placeholder="Item description"
                      className="w-full text-sm"
                      testId={`item-desc-${catIdx}-${itemIdx}`}
                    />
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <div className="relative w-[140px]">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-[#6b7280]/50 text-sm">$</span>
                      <input
                        type="text"
                        value={focusedCommentId === item.id
                          ? (item.comment || 0)
                          : Number(item.comment || 0).toFixed(2)}
                        onChange={e => {
                          const raw = e.target.value.replace(/,/g, '');
                          if (raw === '' || !isNaN(raw)) {
                            updateItem(cat.id, item.id, 'comment', raw === '' ? 0 : parseFloat(raw));
                          }
                        }}
                        onFocus={() => setFocusedCommentId(item.id)}
                        onBlur={() => setFocusedCommentId(null)}
                        className="editable-field amount-input pl-6 text-[#6b7280] bg-transparent text-sm w-full"
                        placeholder="0.00"
                        title="Internal cost — not shown in exports"
                        data-testid={`item-comment-${catIdx}-${itemIdx}`}
                      />
                    </div>
                    <button
                      onClick={() => deleteItem(cat.id, item.id)}
                      className="text-[#6b7280] hover:text-[#dc2626] transition-colors flex-shrink-0"
                      data-testid={`item-delete-${catIdx}-${itemIdx}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
              <button
                onClick={() => addItem(cat.id)}
                className="flex items-center gap-1 text-sm text-[#c9a84c] hover:text-[#b8973e] px-8 py-2 transition-colors"
                data-testid={`add-item-btn-${catIdx}`}
              >
                <Plus className="w-4 h-4" />
                Add Item
              </button>
            </div>
          )}
        </div>
      ))}

      <Button
        variant="outline"
        size="sm"
        onClick={addCategory}
        className="mt-4 border-[#c9a84c] text-[#c9a84c] hover:bg-[#c9a84c]/10"
        data-testid="add-category-btn"
      >
        <Plus className="w-4 h-4 mr-1" />
        Add Category
      </Button>
    </div>
  );
}
